import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  FormControlLabel,
  Switch,
  Alert,
  Tabs,
  Tab,
  Box,
  Typography,
} from '@mui/material';
import { createEmailTemplate, updateEmailTemplate } from '../../../controllers/emailTemplateController';

const defaultState = {
  name: '',
  subject: '',
  description: '',
  body: '',
  contentType: 0,
  isSystemTemplate: false,
  isActive: true,
};

const EmailTemplateFormDialog = ({ open, onClose, template, mode = 'create', onSaved }) => {
  const isEdit = mode === 'edit' && template;
  const [formState, setFormState] = useState(defaultState);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [contentTab, setContentTab] = useState('edit');

  useEffect(() => {
    if (open) {
      setFormState(
        isEdit
          ? {
              name: template?.name || '',
              subject: template?.subject || '',
              description: template?.description || '',
              body: template?.body || '',
              contentType:
                typeof template?.contentType === 'string'
                  ? template.contentType.toLowerCase() === 'plaintext'
                    ? 1
                    : 0
                  : Number(template?.contentType ?? 0),
              isSystemTemplate: Boolean(template?.isSystemTemplate),
              isActive: Boolean(template?.isActive ?? true),
            }
          : defaultState
      );
      setError('');
      setContentTab('edit');
    } else {
      setFormState(defaultState);
      setSubmitting(false);
      setError('');
      setContentTab('edit');
    }
  }, [open, isEdit, template]);

  const contentTypeOptions = useMemo(
    () => [
      { label: 'HTML', value: 0 },
      { label: 'Düz Metin', value: 1 },
    ],
    []
  );

  // {{content}} placeholder kontrolü
  const validateContentPlaceholder = (body) => {
    if (!body || typeof body !== 'string') {
      return { valid: false, message: 'İçerik alanı boş olamaz.' };
    }

    // Tüm placeholder'ları bul ({{...}} formatındaki)
    const placeholderRegex = /\{\{([^}]+)\}\}/g;
    const matches = body.match(placeholderRegex);
    
    if (!matches || matches.length === 0) {
      return { 
        valid: false, 
        message: 'İçerikte zorunlu olan {{content}} placeholder\'ı bulunamadı. Lütfen içeriğinize {{content}} ekleyin.' 
      };
    }

    // Sadece {{content}} olmalı, başka placeholder olmamalı
    const hasContent = matches.some(match => match.toLowerCase() === '{{content}}');
    const hasOtherPlaceholders = matches.some(match => match.toLowerCase() !== '{{content}}');

    if (!hasContent) {
      return { 
        valid: false, 
        message: 'İçerikte zorunlu olan {{content}} placeholder\'ı bulunamadı. Lütfen içeriğinize {{content}} ekleyin.' 
      };
    }

    if (hasOtherPlaceholders) {
      return { 
        valid: false, 
        message: 'İçerikte sadece {{content}} placeholder\'ı kullanılabilir. Lütfen diğer placeholder\'ları kaldırın.' 
      };
    }

    return { valid: true };
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (submitting) return;
    
    // {{content}} validasyonu
    const validation = validateContentPlaceholder(formState.body);
    if (!validation.valid) {
      setError(validation.message);
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const payload = {
        name: formState.name.trim(),
        subject: formState.subject.trim(),
        description: formState.description?.trim() || null,
        body: formState.body,
        contentType: Number(formState.contentType),
        isSystemTemplate: Boolean(formState.isSystemTemplate),
        isActive: Boolean(formState.isActive),
        placeholders: '{{content}}', // Otomatik olarak {{content}} set ediyoruz
      };
      let result;
      if (isEdit) {
        result = await updateEmailTemplate({ ...payload, id: template.id });
      } else {
        result = await createEmailTemplate(payload);
      }
      onSaved?.(result);
      onClose?.();
    } catch (err) {
      setError(err?.message || 'İşlem başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>{isEdit ? 'E-posta Şablonunu Düzenle' : 'Yeni E-posta Şablonu'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Şablon Adı"
              value={formState.name}
              onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))}
              required
              autoFocus
            />
            <TextField
              label="Konu"
              value={formState.subject}
              onChange={(e) => setFormState((prev) => ({ ...prev, subject: e.target.value }))}
              required
            />
            <TextField
              label="Açıklama"
              value={formState.description}
              onChange={(e) => setFormState((prev) => ({ ...prev, description: e.target.value }))}
            />
            {Number(formState.contentType) === 1 ? (
              <TextField
                label="İçerik"
                value={formState.body}
                onChange={(e) => setFormState((prev) => ({ ...prev, body: e.target.value }))}
                required
                multiline
                minRows={10}
                placeholder="Merhaba, {{content}}"
                helperText="İçerikte zorunlu olarak {{content}} placeholder'ı bulunmalıdır. Başka placeholder kullanılamaz."
              />
            ) : (
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Tabs
                    value={contentTab}
                    onChange={(e, val) => setContentTab(val)}
                    sx={{ minHeight: 32, '& .MuiTab-root': { minHeight: 32 } }}
                  >
                    <Tab value="edit" label="HTML Düzenle" />
                    <Tab value="preview" label="Önizleme" />
                  </Tabs>
                  <Typography variant="caption" color="text.secondary">
                    Zorunlu: İçerikte {'{{content}}'} placeholder'ı bulunmalıdır.
                  </Typography>
                </Stack>
                {contentTab === 'edit' ? (
                  <TextField
                    label="HTML İçerik"
                    value={formState.body}
                    onChange={(e) => setFormState((prev) => ({ ...prev, body: e.target.value }))}
                    required
                    multiline
                    minRows={12}
                    placeholder="<h1>Merhaba</h1><p>{{content}}</p>"
                    helperText="İçerikte zorunlu olarak {{content}} placeholder'ı bulunmalıdır. Başka placeholder kullanılamaz."
                  />
                ) : (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      minHeight: 200,
                      maxHeight: 400,
                      overflow: 'auto',
                      backgroundColor: 'background.paper',
                    }}
                    dangerouslySetInnerHTML={{ __html: formState.body || '<p>Önizleme için HTML içeriği girin.</p>' }}
                  />
                )}
              </Stack>
            )}
            <TextField
              select
              label="İçerik Türü"
              value={formState.contentType}
              onChange={(e) => setFormState((prev) => ({ ...prev, contentType: Number(e.target.value) }))}
              required
              sx={{ maxWidth: 240 }}
            >
              {contentTypeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="body2" component="div">
                <strong>Önemli:</strong> İçerikte zorunlu olarak <code>{'{{content}}'}</code> placeholder'ı kullanılmalıdır. 
                Başka placeholder kullanılamaz. İçerik gönderilirken <code>{'{{content}}'}</code> otomatik olarak duyuru içeriği ile değiştirilecektir.
              </Typography>
            </Alert>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formState.isActive}
                    onChange={(e) => setFormState((prev) => ({ ...prev, isActive: e.target.checked }))}
                  />
                }
                label="Aktif"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formState.isSystemTemplate}
                    onChange={(e) => setFormState((prev) => ({ ...prev, isSystemTemplate: e.target.checked }))}
                  />
                }
                label="Sistem Şablonu"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            İptal
          </Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? 'Kaydediliyor...' : isEdit ? 'Güncelle' : 'Oluştur'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EmailTemplateFormDialog;


